import * as React from 'react';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { useLocalizer, useLocalizerFormat } from './hooks/useLocalizer';

interface AIWebRetrievalUrlFieldProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    localizer?: IStringLocalizer;
}

const splitWebRetrievalUrls = (value: string): string[] => value
    .split(/[;\r\n]+/)
    .map(url => url.trim())
    .filter(url => url.length > 0);

export const getValidWebRetrievalUrls = (value: string): string[] => splitWebRetrievalUrls(value)
    .filter(url => isValidWebRetrievalUrl(url));

const getInvalidWebRetrievalUrls = (value: string): string[] => splitWebRetrievalUrls(value)
    .filter(url => !isValidWebRetrievalUrl(url));

const isValidWebRetrievalUrl = (value: string): boolean => {
    try {
        const url = new URL(value);
        return (url.protocol === 'http:' || url.protocol === 'https:')
            && url.hostname.includes('.')
            && !url.hostname.startsWith('.')
            && !url.hostname.endsWith('.');
    } catch {
        return false;
    }
};

/**
 * Optional, semicolon/newline-delimited public sources for web retrieval.
 */
export const AIWebRetrievalUrlField: React.FC<AIWebRetrievalUrlFieldProps> = ({
    value,
    onChange,
    disabled = false,
    localizer
}) => {
    const localize = useLocalizer(localizer);
    const localizeFormat = useLocalizerFormat(localizer);
    const invalidUrls = getInvalidWebRetrievalUrls(value);

    return (
        <div className="form-group" style={{ marginBottom: '15px' }}>
            <textarea
                id="aiWebRetrievalUrls"
                className={`form-control${invalidUrls.length > 0 ? ' is-invalid' : ''}`}
                rows={3}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                disabled={disabled}
                style={invalidUrls.length > 0 ? { borderColor: '#dc3545' } : undefined}
                placeholder={localize('Optional: add specific URLs to search (semicolon or new line separated)')}
                aria-describedby="aiWebRetrievalUrlsHelp aiWebRetrievalUrlsError"
            />
            <small id="aiWebRetrievalUrlsHelp" className="form-text text-muted">
                {localize('Optional — the agent still performs a broad web search. Listed URLs are searched in addition.')}
            </small>
            {invalidUrls.length > 0 && (
                <div id="aiWebRetrievalUrlsError" className="invalid-feedback" role="alert" style={{ color: '#dc3545' }}>
                    <span className="glyphicon glyphicon-exclamation-sign" aria-hidden="true" style={{ marginRight: '4px' }}></span>
                    {localizeFormat(
                        'Invalid URLs — web search cannot run on: {0}. Please enter valid URLs (e.g., https://example.com).',
                        invalidUrls.join(', ')
                    )}
                </div>
            )}
        </div>
    );
};

export default AIWebRetrievalUrlField;
